import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Empty, List, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EntityCardOverlayFrame } from '../../components/entityCard/EntityCardOverlayFrame';
import { useReferenceCardOverlay } from '../../hooks/useReferenceCardOverlay';
import type { ColumnsType } from 'antd/es/table';
import { ApplicationCell, ExecutionCell, ExternalApplicationCell, PageLayout, StatusCell } from '../../ui';
import { EMPTY_FIELD, emptyListMessage, innSubtitleLine } from '../../ui/entityCard/entityCardLabels';
import useHasPermission from '../../hooks/useHasPermission';
import { useGetContractorByIdQuery, type Contractor, type ContractorRole } from '../../store/api/referencesApi';
import CreateContractorModal from '../../components/common/CreateContractorModal';
import ContractorContractsPanel from './ContractorContractsPanel';
import { useContractorClientRelated } from '../../hooks/useContractorClientRelated';
import type { Application, Route } from '../../types';
import { formatDate } from '../../utils/applicationDisplayFormat';
import {
  ENTITY_CARD_DASH,
  entityCardRouteDirectionLabel,
  entityCardRouteLoadingDate,
  entityCardRouteUnloadingDate
} from '../../utils/entityCardRouteUtils';
import './ContractorCardPage.css';

const { Text } = Typography;

const ROLE_LABELS: Record<ContractorRole, string> = {
  client: 'Клиент',
  expeditor: 'Экспедитор',
  shipper: 'Грузоотправитель',
  consignee: 'Грузополучатель'
};

function roleLabel(role: string): string {
  if (role === 'carrier') return 'Перевозчик (устар.)';
  return ROLE_LABELS[role as ContractorRole] ?? role;
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return EMPTY_FIELD;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY_FIELD;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Контакты: поля контрагента и задел под расширение */
function buildContacts(c: Contractor): { key: string; name: string; phone?: string; email?: string }[] {
  const rows: { key: string; name: string; phone?: string; email?: string }[] = [];
  const hasPerson =
    Boolean((c.contactPerson && c.contactPerson.trim()) || (c.phone && c.phone.trim()) || (c.email && c.email.trim()));
  if (hasPerson) {
    rows.push({
      key: 'primary',
      name: (c.contactPerson && c.contactPerson.trim()) || 'Контакт',
      phone: (c.phone && c.phone.trim()) || undefined,
      email: (c.email && c.email.trim()) || undefined
    });
  }
  return rows;
}

const cardSurface: React.CSSProperties = {
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: 8
};

const sectionTitle = (t: string) => <Typography.Title level={5} style={{ margin: 0 }}>{t}</Typography.Title>;

const ContractorCardPageContent: React.FC = () => {
  const navigate = useNavigate();
  const { isOverlay, close } = useReferenceCardOverlay();
  const { id: idParam } = useParams<{ id: string }>();
  const idNum = idParam ? parseInt(idParam, 10) : NaN;
  const skip = Number.isNaN(idNum);

  const canViewReferences = useHasPermission('references.view');
  const canEditReferences = useHasPermission('references.edit');
  const canCreateReferences = useHasPermission('references.create');
  const canModifyReferences = canEditReferences || canCreateReferences;

  const { data, isLoading, isError, error, refetch } = useGetContractorByIdQuery(idNum, {
    skip: skip || !canViewReferences
  });

  const isClient = Boolean(data?.roles?.includes('client'));
  const { applications, routes, loading: relatedLoading, applicationsError } = useContractorClientRelated(
    idNum,
    canViewReferences && !skip && isClient
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editContractorSnapshot, setEditContractorSnapshot] = useState<Contractor | null>(null);

  const contacts = useMemo(() => (data ? buildContacts(data) : []), [data]);

  const routeColumns: ColumnsType<Route> = useMemo(() => {
    const clientId = data?.id;
    const clientName = data?.name?.trim() || ENTITY_CARD_DASH;
    return [
      {
        title: '№ маршрута',
        key: 'num',
        width: 120,
        render: (_: unknown, r: Route) => {
          const text = r.number?.trim() ? r.number : String(r.id);
          return (
            <Link to={`/routes/${r.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
              {text}
            </Link>
          );
        }
      },
      {
        title: 'Маршрут',
        key: 'dir',
        ellipsis: true,
        render: (_: unknown, r: Route) => entityCardRouteDirectionLabel(r)
      },
      {
        title: 'Клиент',
        key: 'cl',
        width: 200,
        ellipsis: true,
        render: (_: unknown, _r: Route) => {
          if (clientId != null) {
            return (
              <Link to={`/clients/${clientId}`} onClick={(e) => e.stopPropagation()}>
                {clientName}
              </Link>
            );
          }
          return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
        }
      },
      {
        title: 'Исполнение',
        key: 'execution',
        width: 200,
        ellipsis: true,
        render: (_: unknown, r: Route) => <ExecutionCell route={r} />
      },
      {
        title: 'Погрузка',
        key: 'pl',
        width: 120,
        align: 'center',
        render: (_: unknown, r: Route) => entityCardRouteLoadingDate(r)
      },
      {
        title: 'Выгрузка',
        key: 'unl',
        width: 120,
        align: 'center',
        render: (_: unknown, r: Route) => entityCardRouteUnloadingDate(r)
      },
      {
        title: 'Статус',
        key: 'st',
        width: 140,
        render: (_: unknown, r: Route) => <StatusCell status={String(r.status ?? '')} />
      },
      {
        title: 'Штрафы',
        key: 'pen',
        width: 160,
        align: 'right',
        render: () => <Text type="secondary">{ENTITY_CARD_DASH}</Text>
      }
    ];
  }, [data?.id, data?.name]);

  const applicationColumns: ColumnsType<Application> = useMemo(
    () => [
      {
        title: 'Заявка',
        key: 'n',
        width: 200,
        render: (_: unknown, a: Application) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ApplicationCell application={a} />
          </div>
        )
      },
      {
        title: 'Клиент',
        key: 'cl',
        width: 200,
        ellipsis: true,
        render: (_: unknown, a: Application) => {
          const id = a.clientId;
          const name = a.client?.name?.trim() || ENTITY_CARD_DASH;
          if (id != null) {
            return (
              <Link to={`/clients/${id}`} onClick={(e) => e.stopPropagation()}>
                {name}
              </Link>
            );
          }
          return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
        }
      },
      {
        title: 'Внешний №',
        key: 'ext',
        width: 220,
        render: (_: unknown, a: Application) => <ExternalApplicationCell application={a} />
      },
      {
        title: 'Погрузка',
        key: 'pl',
        width: 120,
        render: (_: unknown, a: Application) => <span>{formatDate(a.plannedLoadingDate)}</span>
      },
      {
        title: 'Выгрузка',
        key: 'unl',
        width: 120,
        render: (_: unknown, a: Application) => <span>{formatDate(a.plannedUnloadingDate)}</span>
      },
      {
        title: 'Статус',
        key: 'st',
        width: 130,
        render: (_: unknown, a: Application) => <StatusCell status={String(a.status ?? '')} />
      }
    ],
    []
  );

  if (!canViewReferences) {
    return (
      <PageLayout title="Контрагент">
        <Card>
          <Text>У вас нет прав для просмотра справочников.</Text>
        </Card>
      </PageLayout>
    );
  }

  if (skip) {
    return (
      <PageLayout title="Контрагент">
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout title="Контрагент" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    const msg =
      (error as { data?: { error?: string } })?.data?.error ||
      'Не удалось загрузить контрагента. Возможно, запись удалена или недоступна.';
    return (
      <PageLayout title="Контрагент">
        <Alert type="error" message={msg} showIcon />
      </PageLayout>
    );
  }

  const cardTab = (
    <div className="contractor-card-grid entity-card-tab-body">
      <Card title={sectionTitle('Основная информация')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="Наименование">{data.name?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Роли">
            {data.roles?.length ? (
              <Space size={[6, 6]} wrap>
                {data.roles.map((r) => (
                  <Tag key={r} color="blue">
                    {roleLabel(r)}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">роли не назначены</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Создан">
            {data.createdAt ? formatDateTime(data.createdAt) : 'Дата создания не заполнена'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Контакты')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        {contacts.length ? (
          <List
            size="small"
            dataSource={contacts}
            renderItem={(item) => (
              <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                <List.Item.Meta
                  title={item.name}
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary">Телефон: {item.phone?.trim() || 'не указан'}</Text>
                      <Text type="secondary">Email: {item.email?.trim() || 'не указан'}</Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Text type="secondary">Контактные данные ещё не заполнены — их можно добавить при редактировании карточки</Text>
        )}
      </Card>

      <Card title={sectionTitle('Реквизиты')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="ИНН">{data.inn?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="КПП">{data.kpp?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="ОГРН">{data.ogrn?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Директор">{data.directorName?.trim() || EMPTY_FIELD}</Descriptions.Item>
          {data.address?.trim() ? (
            <Descriptions.Item label="Адрес">{data.address}</Descriptions.Item>
          ) : (
            <Descriptions.Item label="Адрес">
              <Text type="secondary">юридический адрес не указан</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Банк')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="Банк">{data.bankName?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="БИК">{data.bik?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Р/с">{data.accountNumber?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="К/с">{data.corrAccount?.trim() || EMPTY_FIELD}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Договоры')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        {data.roles?.includes('client') ? (
          <ContractorContractsPanel clientId={data.id} canAdd={canModifyReferences} />
        ) : (
          <Text type="secondary">Раздел договоров доступен для контрагентов с ролью «Клиент»</Text>
        )}
      </Card>
    </div>
  );

  const penaltiesTab = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="Сводные штрафы по маршрутам смотрите во вкладке «Штрафы» в карточке каждого маршрута."
    />
  );

  const routesTab =
    !isClient ? (
      <Text type="secondary">Список маршрутов доступен для контрагента с ролью «Клиент»</Text>
    ) : applicationsError ? (
      <Alert type="warning" message="Не удалось загрузить заявки для построения списка маршрутов" showIcon />
    ) : relatedLoading ? (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    ) : routes.length === 0 ? (
      <Text type="secondary">{emptyListMessage('маршрутов по заявкам этого клиента')}</Text>
    ) : (
      <Table<Route>
        className="table-card-rows entity-card-table"
        size="small"
        rowKey="id"
        columns={routeColumns}
        dataSource={routes}
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        onRow={(record) => ({
          onClick: () => navigate(`/routes/${record.id}`),
          style: { cursor: 'pointer' }
        })}
      />
    );

  const applicationsTab =
    !isClient ? (
      <Text type="secondary">Список заявок доступен для контрагента с ролью «Клиент»</Text>
    ) : applicationsError ? (
      <Alert type="warning" message="Не удалось загрузить заявки" showIcon />
    ) : relatedLoading ? (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    ) : applications.length === 0 ? (
      <Text type="secondary">{emptyListMessage('заявок у этого клиента')}</Text>
    ) : (
      <Table
        className="table-card-rows entity-card-table"
        size="small"
        rowKey="id"
        columns={applicationColumns}
        dataSource={applications}
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        onRow={(record) => ({
          onClick: () => navigate(`/applications/${record.id}`),
          style: { cursor: 'pointer' }
        })}
      />
    );

  const headerSubtitle = (
    <Space direction="vertical" size={4}>
      <Text type="secondary">{innSubtitleLine(data.inn)}</Text>
      {isClient ? (
        relatedLoading ? (
          <Text type="secondary">Связанные данные: загрузка…</Text>
        ) : (
          <Text type="secondary">
            Заявок: {applications.length} · Маршрутов: {routes.length}
          </Text>
        )
      ) : (
        <Text type="secondary">Роли: {data.roles?.map(roleLabel).join(', ') || 'не указаны'}</Text>
      )}
    </Space>
  );

  const tabItems = [
    { key: 'card', label: 'Карточка', children: cardTab },
    { key: 'penalties', label: 'Штрафы', children: penaltiesTab },
    { key: 'routes', label: `Маршруты (${isClient ? routes.length : 0})`, children: routesTab },
    { key: 'applications', label: `Заявки (${isClient ? applications.length : 0})`, children: applicationsTab }
  ];

  return (
    <div className="contractor-card-page">
      <PageLayout
        titleLevel={2}
        title={data.name}
        subtitle={headerSubtitle}
        actions={
          isOverlay || canModifyReferences ? (
            <Space>
              {isOverlay && (
                <Button type="default" onClick={close}>
                  Закрыть
                </Button>
              )}
              {canModifyReferences ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    if (data) setEditContractorSnapshot(data);
                    setEditOpen(true);
                  }}
                >
                  Редактировать
                </Button>
              ) : null}
            </Space>
          ) : undefined
        }
      >
        <Tabs defaultActiveKey="card" className="entity-card-tabs" items={tabItems} />
      </PageLayout>

      <CreateContractorModal
        isOpen={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditContractorSnapshot(null);
        }}
        onSuccess={() => {
          void refetch();
          setEditOpen(false);
          setEditContractorSnapshot(null);
        }}
        contractor={editContractorSnapshot}
        title="Редактировать контрагента"
      />
    </div>
  );
};

const ContractorCardPage: React.FC = () => (
  <EntityCardOverlayFrame>
    <ContractorCardPageContent />
  </EntityCardOverlayFrame>
);

export default ContractorCardPage;
